import { BlockRecord } from "models/view-models/block-record";
import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useParams } from "react-router";
import BlockService, {
    BlockCreateParams,
} from "utilities/services/blocks/block-service";
import { NotebookPageContentBlock } from "constants/notebooks/notebook-page-content-blocks";
import usePageBlockError from "utilities/hooks/domain/notebooks/pages/use-page-block-errors";
import { useReadonlyContext } from "utilities/contexts/readonly/use-readonly-context";
import { CollectionUtils } from "@rsm-hcd/javascript-core";
import PageBlockService from "utilities/services/blocks/page-block-service";
import { useSortBlocks } from "utilities/hooks/domain/notebooks/pages/use-sort-blocks";
import { DragStartEvent } from "@dnd-kit/core";
import SharedPageBlockService from "utilities/services/public/blocks/shared-page-block-service";
import usePopulatedBlocks from "utilities/hooks/domain/notebooks/pages/use-populated-blocks";
import useLazyLoadPageBlocks from "./use-lazy-load-page-blocks";
import UsePageLimits from "pages/notebooks/use-page-limits";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "i18next";
import { usePageBlockState } from "utilities/hooks/domain/notebooks/pages/use-page-block-state";
interface UsePageBlocksArgs {
    isPublic?: boolean;
    isPrinting?: boolean;
    pageGuid?: string;
    recipientGuid?: string;
    onAnyBlockInListUpdated?: () => Promise<void>;
}
export interface UsePageBlocks {
    activeBlock?: BlockRecord;
    deleteBlock: (block: BlockRecord) => Promise<void>;
    draggingBlockId?: number;
    hasNoBlocks: boolean;
    isSaving: boolean;
    loading?: boolean;
    moveBlocks: (updatedBlocks: BlockRecord[]) => Promise<void>;
    moveBlockToBottom: (blockToMove: BlockRecord) => Promise<void>;
    moveBlockToTop: (blockToMove: BlockRecord) => Promise<void>;
    onActivateBlock: (block: BlockRecord) => void;
    onDeactivateBlock: () => void;
    onDragEnd: () => void;
    onDragStart: (event: DragStartEvent) => void;
    populatedBlocks: BlockRecord[];
    setBlocks: Dispatch<SetStateAction<BlockRecord[]>>;
    sortedBlocks: BlockRecord[];
    startNewBlock: (
        type: NotebookPageContentBlock,
        afterBlockId?: number
    ) => Promise<void>;
    startNewBlockAtEnd: (type: NotebookPageContentBlock) => Promise<void>;
    updateBlockInList: (updatedBlock: BlockRecord) => void;
    fetchingBlocks: boolean;
    hasMoreBlocks: boolean;
    fetchNextSetOfBlocks: () => Promise<void>;
    observerRef: (node: HTMLElement | null) => void;
    refreshBlocksInListById: () => Promise<void>;
}

const usePageBlocks = (args?: UsePageBlocksArgs): UsePageBlocks => {
    const params = useParams<{
        notebookId: string;
        pageId: string;
    }>();

    const pageId = Number(params.pageId);
    const pageGuid = args?.pageGuid;
    const recipientGuid = args?.recipientGuid;
    const isPrinting = args?.isPrinting ?? false;
    const onAnyBlockInListUpdated = args?.onAnyBlockInListUpdated;
    const [isSaving, setIsSaving] = useState(false);
    const { list: getBlocksByPageIdApi } = PageBlockService.useGetByPage();
    const { list: getBlocksByPageGuidApi } =
        SharedPageBlockService.useGetByPage();
    const { create } = BlockService.useCreate();
    const { patch } = BlockService.useMove();
    const { delete: deleteBlockApi } = BlockService.useDelete();
    const { showError } = usePageBlockError();
    const {
        pageLimits,
        setPageBlockLimitReached,
        setCodeReferenceBlockLimitReached,
        codeReferenceBlockLimitReached,
    } = UsePageLimits();
    const { blocks, setBlocks, setActiveBlockById, activeBlock } =
        usePageBlockState();
    const [backendPageIndex, setBackendPageIndex] = useState(0);
    const [hasMoreBlocks, setHasMoreBlocks] = useState(true);
    const [fetchingBlocks, setFetchingBlocks] = useState(false);
    const [draggingBlockId, setDraggingBlockId] = useState<number>();
    const { skipIfIsReadonly } = useReadonlyContext();
    const { onBlocksReordered, sortedBlocks } = useSortBlocks(blocks);
    const { populatedBlocks } = usePopulatedBlocks(sortedBlocks);
    const [loading, setLoading] = useState(true);

    const TAKE = isPrinting ? 10000 : 50;

    const onDragStart = (event: DragStartEvent) => {
        const blockId = event.active?.id;
        setDraggingBlockId(blockId as number);
    };
    const onDragEnd = () => {
        setDraggingBlockId(undefined);
    };

    const preventNewBlock = () => {
        if (
            pageLimits?.blocksLimit &&
            pageLimits?.blocksLimit < blocks.length
        ) {
            ToastManager.warn(t("exceedsBlockLimit"));
            return true;
        }
        if (codeReferenceBlockLimitReached) {
            return true;
        }
        return false;
    };

    const startNewBlock = async (
        type: NotebookPageContentBlock,
        afterBlockId?: number
    ) => {
        if (preventNewBlock()) {
            return;
        }
        setIsSaving(true);

        try {
            const blockCreateParams: BlockCreateParams = {
                pageId: pageId,
                type: type,
                afterBlockId: afterBlockId,
            };

            const response = await create(blockCreateParams);

            if (response?.result == null || response.resultObject == null) {
                throw new Error("No block was returned upon creation.");
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            const createdBlock = response.resultObject;

            setBlocks((prev) => {
                // Find first block whose sortIndex is greater than the new block
                const insertIndex = prev.findIndex(
                    (b) => b.sortIndex > createdBlock.sortIndex
                );

                // If none found, append to the end
                if (insertIndex === -1) {
                    return [...prev, createdBlock];
                }

                return [
                    ...prev.slice(0, insertIndex),
                    createdBlock,
                    ...prev.slice(insertIndex),
                ];
            });
            onActivateBlock(createdBlock);
            await onAnyBlockInListUpdated?.();
        } catch {
            showError("creating", "block");
        } finally {
            checkBlockLimit();
            checkCodeReferenceBlockLimit();
            setIsSaving(false);
        }
    };

    const startNewBlockAtEnd = async (type: NotebookPageContentBlock) => {
        await startNewBlock(type, sortedBlocks.at(-1)?.id);
    };

    const checkCodeReferenceBlockLimit = useCallback(() => {
        const codeReferenceBlockLength = blocks.filter(
            (b) => b.type === NotebookPageContentBlock.CodeReference
        ).length;
        if (
            pageLimits?.codeReferenceBlockLimit &&
            pageLimits?.codeReferenceBlockLimit <= codeReferenceBlockLength
        ) {
            setCodeReferenceBlockLimitReached(true);

            return;
        }
        setCodeReferenceBlockLimitReached(false);
    }, [
        blocks,
        pageLimits?.codeReferenceBlockLimit,
        setCodeReferenceBlockLimitReached,
    ]);

    const checkBlockLimit = useCallback(() => {
        if (
            pageLimits?.blocksLimit &&
            pageLimits?.blocksLimit <= blocks.length
        ) {
            setPageBlockLimitReached(true);
            return;
        }
        setPageBlockLimitReached(false);
    }, [blocks.length, pageLimits?.blocksLimit, setPageBlockLimitReached]);

    const deleteBlock = skipIfIsReadonly(async (block: BlockRecord) => {
        setIsSaving(true);
        try {
            await deleteBlockApi(block.id!, { id: block.id });
            setActiveBlockById(undefined);
            await loadBlocks();
            await onAnyBlockInListUpdated?.();
        } catch (error) {
            showError("deleting", "block");
        } finally {
            checkBlockLimit();
            checkCodeReferenceBlockLimit();
            setIsSaving(false);
        }
    });

    const loadBlocks = useCallback(
        async (showLoader: boolean = true): Promise<void> => {
            if (!args?.isPublic && (isNaN(pageId) || pageId <= 0)) {
                setBlocks([]);
                setLoading(false);
                return;
            }

            try {
                if (showLoader) {
                    setLoading(true);
                }

                const getBlocksResponse =
                    args?.isPublic || pageGuid
                        ? await getBlocksByPageGuidApi(
                              {
                                  pageGuid: pageGuid!,
                              },
                              { recipient: recipientGuid, skip: 0, take: TAKE }
                          )
                        : await getBlocksByPageIdApi(
                              { pageId },
                              { skip: 0, take: TAKE }
                          );
                const blocks = getBlocksResponse.resultObjects ?? [];

                setBlocks(blocks);
            } catch {
                showError("loading", "block");
            } finally {
                checkBlockLimit();
                checkCodeReferenceBlockLimit();
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            args?.isPublic,
            pageId,
            pageGuid,
            getBlocksByPageGuidApi,
            recipientGuid,
            TAKE,
            getBlocksByPageIdApi,
            showError,
            checkBlockLimit,
        ]
    );

    const fetchNextSetOfBlocks = useCallback(async (): Promise<void> => {
        if (!args?.isPublic && (isNaN(pageId) || pageId <= 0)) return;
        if (!hasMoreBlocks || fetchingBlocks) return;

        try {
            setFetchingBlocks(true);

            const nextPageIndex = backendPageIndex + 1;

            const getBlocksResponse =
                args?.isPublic || pageGuid
                    ? await getBlocksByPageGuidApi(
                          {
                              pageGuid: pageGuid!,
                          },
                          {
                              recipient: recipientGuid,
                              skip: nextPageIndex,
                              take: TAKE,
                          }
                      )
                    : await getBlocksByPageIdApi(
                          { pageId },
                          { skip: nextPageIndex, take: TAKE }
                      );

            const pageBlocks = getBlocksResponse.resultObjects ?? [];

            if (pageBlocks.length > 0) {
                setBlocks((prev) => [...prev, ...pageBlocks]);
                setBackendPageIndex(nextPageIndex);
                setHasMoreBlocks(pageBlocks.length === TAKE);
            } else {
                setHasMoreBlocks(false);
            }
        } catch {
            showError("loading", "block");
        } finally {
            setFetchingBlocks(false);
        }
    }, [
        TAKE,
        args?.isPublic,
        backendPageIndex,
        fetchingBlocks,
        getBlocksByPageGuidApi,
        getBlocksByPageIdApi,
        hasMoreBlocks,
        pageGuid,
        pageId,
        recipientGuid,
        setBlocks,
        showError,
    ]);

    const updateBlockInList = skipIfIsReadonly((updatedBlock: BlockRecord) => {
        setBlocks((previousBlocks: BlockRecord[]): BlockRecord[] => {
            const indexToReplace = previousBlocks.findIndex(
                (block) => block.id === updatedBlock.id
            );

            if (indexToReplace === -1) {
                if (updatedBlock.configuration == null) {
                    return [...previousBlocks];
                }
                return [...previousBlocks, updatedBlock];
            }

            return previousBlocks.toSpliced(indexToReplace, 1, updatedBlock);
        });
    });

    const moveBlocks = skipIfIsReadonly(
        async (updatedBlocks: BlockRecord[]): Promise<void> => {
            const updatedIds = await onBlocksReordered(updatedBlocks);

            setBlocks((previousBlocks: BlockRecord[]): BlockRecord[] => {
                return updatedIds
                    .map((id) => previousBlocks.find((b) => b.id === id))
                    .filter((b) => b != null);
            });

            await loadBlocks(false);
            await onAnyBlockInListUpdated?.();
        }
    );

    const moveBlockToTop = skipIfIsReadonly(
        async (blockToMove: BlockRecord) => {
            try {
                await patch({}, { movingBlockId: blockToMove.id! });
                await loadBlocks();
                await onAnyBlockInListUpdated?.();
            } catch (e) {
                showError("moving", "block");
            }
        }
    );

    const moveBlockToBottom = skipIfIsReadonly(
        async (blockToMove: BlockRecord) => {
            try {
                const blockToMoveBelow = blocks.sort(
                    (a, b) => a.sortIndex - b.sortIndex
                )[blocks.length - 1];
                await patch(
                    {},
                    {
                        movingBlockId: blockToMove.id!,
                        precedingBlockId: blockToMoveBelow.id,
                    }
                );
                await loadBlocks();
                await onAnyBlockInListUpdated?.();
            } catch (e) {
                showError("moving", "block");
            }
        }
    );

    const refreshBlocksInListById = useCallback(async () => {
        const upToDateBlockList = await getBlocksByPageIdApi({ pageId });
        setBlocks((_: BlockRecord[]) => {
            return upToDateBlockList.resultObjects.map((updatedBlock) => {
                return activeBlock && activeBlock.id === updatedBlock?.id
                    ? activeBlock
                    : updatedBlock;
            });
        });
    }, [activeBlock, getBlocksByPageIdApi, pageId, setBlocks]);

    const onActivateBlock = useCallback(
        (block: BlockRecord) => {
            setActiveBlockById(block.id);
        },
        [setActiveBlockById]
    );

    const onDeactivateBlock = () => {
        setActiveBlockById(undefined);
    };

    const hasNoBlocks = CollectionUtils.isEmpty(blocks);

    useEffect(() => {
        loadBlocks();
    }, [loadBlocks]);

    const { observerRef } = useLazyLoadPageBlocks({
        isLoading: fetchingBlocks,
        noRecordsRemaining: !hasMoreBlocks,
        onPageNext: fetchNextSetOfBlocks,
    });

    return {
        activeBlock,
        deleteBlock,
        draggingBlockId,
        hasNoBlocks,
        isSaving,
        loading,
        moveBlocks,
        moveBlockToBottom,
        moveBlockToTop,
        onActivateBlock,
        onDeactivateBlock,
        onDragEnd,
        onDragStart,
        populatedBlocks,
        setBlocks,
        sortedBlocks,
        startNewBlock,
        startNewBlockAtEnd,
        updateBlockInList,
        fetchingBlocks,
        hasMoreBlocks,
        fetchNextSetOfBlocks,
        observerRef,
        refreshBlocksInListById,
    };
};

export default usePageBlocks;
